import {ChangeEvent, ReactElement} from 'react';
import AddIcon from '@mui/icons-material/Add';
import {Alert, Box, Button, Paper, TextField, Tooltip, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Project} from 'interfaces/Project';
import {System} from 'interfaces/System';
import {UseConfiguratorActions} from 'views/Calculator/hooks/useConfigurator/useConfigurator';
import {UseSaveProject} from 'views/Calculator/hooks/useConfigurator/useSaveProject';
import ProjectHeader from './components/ProjectHeader';
import ProjectSections from './components/ProjectSections';
import useReportAvailability from './hooks/useReportAvailability';

type Props = {
  configuratorActions: UseConfiguratorActions;
  isGeneratingPdf: boolean;
  onGeneratePdf: () => void;
  onProjectAction: () => void;
  project: Project;
  saveProject: UseSaveProject;
  systems: System[];
};

export default function ProjectConfiguratorView({
  configuratorActions,
  isGeneratingPdf,
  onGeneratePdf,
  onProjectAction,
  project,
  saveProject,
  systems
}: Props): ReactElement {
  const {t} = useTranslation('common');

  const {canGenerate, disabledReason} = useReportAvailability(project);

  function handleAddSection() {
    configuratorActions.addSection(t('common:sectionIndex', {index: project.sections.length + 1}));
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        alignItems: 'flex-end'
      }}
    >
      <Box sx={{width: '100%', display: 'flex', flexDirection: 'column', gap: 3}}>
        {project.hasDeprecatedInfo && (
          <Alert severity="info" onClose={() => configuratorActions.markDepecreatedAlertAsView()}>
            {t('common:projectHasDeprecatedInfo')}
          </Alert>
        )}
        <Box sx={{display: 'flex', flexDirection: 'column', gap: {xs: 3, sm: 5}}}>
          <ProjectHeader
            saveProject={saveProject}
            project={project}
            onProjectAction={onProjectAction}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: {xs: 'column', sm: 'row'},
              gap: 1,
              alignItems: 'flex-start'
            }}
          >
            <TextField
              id="projectName"
              label={t('common:name')}
              value={project.name}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                configuratorActions.updateProjectName(event.target.value)
              }
              sx={{width: {xs: '100%', sm: 220}}}
            />
            <TextField
              id="projectDescription"
              label={t('common:description')}
              value={project.description ?? ''}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                configuratorActions.updateProjectDescription(event.target.value)
              }
              fullWidth
              sx={{flexGrow: 1}}
            />
          </Box>
        </Box>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: {xs: 'column', sm: 'row'},
              justifyContent: {sm: 'space-between'},
              alignItems: {xs: 'flex-start', sm: 'center'},
              gap: {xs: 1.5, sm: 2}
            }}
          >
            <Typography
              sx={{
                fontSize: {xs: 22, sm: '1.75rem'},
                lineHeight: 1.334,
                fontWeight: 400,
                color: 'ctaBlack'
              }}
            >
              {t('common:selectCablesAndTrays')}
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              endIcon={<AddIcon />}
              onClick={handleAddSection}
              sx={{flexShrink: 0}}
            >
              {t('common:addSection')}
            </Button>
          </Box>
          <Typography sx={{color: 'ctaDark'}}>{t('common:organizeBySections')}</Typography>
        </Box>
        {project.sections.length > 0 && (
          <Box sx={{width: '100%'}}>
            <ProjectSections
              configuratorActions={configuratorActions}
              systems={systems}
              sections={project.sections}
              onSaveProject={(onSuccess) => saveProject.send(project, onSuccess)}
            />
          </Box>
        )}
      </Box>
      <Tooltip title={canGenerate ? '' : disabledReason}>
        <span>
          <Button
            variant="contained"
            color="primary"
            onClick={() => onGeneratePdf()}
            disabled={isGeneratingPdf || !canGenerate}
          >
            {isGeneratingPdf ? t('common:generatingPdf') : t('common:generatePdf')}
          </Button>
        </span>
      </Tooltip>
    </Paper>
  );
}
